<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { UserService } from '@/services/user.service';
import { useBodyScrollLock } from '@/composables/useBodyScrollLock';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'created', userId: string): void;
}>();

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();

useBodyScrollLock(toRef(props, 'open'));

// ── Commerces (pour l'assignation) ─────────────────────────────────────────────
const { data: restaurantsData } = useQuery({
  queryKey: ['cu-restaurants'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    return res.data;
  },
  staleTime: 5 * 60_000,
});
const restaurants = computed<any[]>(() => {
  const d = restaurantsData.value;
  const list = d?.data ?? d?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});

// ── Organisations (pour l'assignation, optionnel) ──────────────────────────────
const { data: organizationsData } = useQuery({
  queryKey: ['cu-organizations'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'organizations/search?pagination=true&pageSize=200&sortBy=name&sortOrder=ASC',
    );
    return res.data;
  },
  staleTime: 5 * 60_000,
});
const organizations = computed<any[]>(() => {
  const d = organizationsData.value;
  const list = d?.data ?? (Array.isArray(d) ? d : []);
  return Array.isArray(list) ? list : [];
});

// ── Étape 1 : compte ────────────────────────────────────────────────────────────
const step = ref<1 | 2>(1);
const form = ref({ email: '', password: '' });
const createError = ref('');
const isCreating = ref(false);
const createdUserId = ref<string | null>(null);
const showPwd = ref(false);

function resetState() {
  step.value = 1;
  form.value = { email: '', password: '' };
  createError.value = '';
  isCreating.value = false;
  createdUserId.value = null;
  showPwd.value = false;
  roleSearch.value = '';
  selectedRoleIds.value = new Set();
  assignStoreId.value = '';
  assignOrgId.value = '';
  assignError.value = '';
  assignSuccess.value = false;
  roleDropdownOpen.value = false;
  rolePage.value = 1;
  storeDropdownOpen.value = false;
  storeSearch.value = '';
  storePage.value = 1;
  orgDropdownOpen.value = false;
  orgSearch.value = '';
  orgPage.value = 1;
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) resetState();
  },
);

function generatePassword() {
  const chars =
    'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*';
  let pwd = '';
  const arr = new Uint8Array(14);
  crypto.getRandomValues(arr);
  arr.forEach((b) => {
    pwd += chars[b % chars.length];
  });
  form.value.password = pwd;
  showPwd.value = true;
}

async function submitAccount() {
  if (!form.value.email.trim() || form.value.password.length < 6) {
    createError.value = 'Email et mot de passe (min. 6 caractères) requis.';
    return;
  }
  isCreating.value = true;
  createError.value = '';
  try {
    const res = await UserService.createUser({
      email: form.value.email.trim(),
      password: form.value.password,
    });
    const uid =
      res?.data?.data?.user?.id ?? res?.data?.data?.id ?? res?.data?.id;
    if (!uid) throw new Error('ID utilisateur introuvable dans la réponse.');
    createdUserId.value = uid;
    step.value = 2;
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    createError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || 'Erreur lors de la création du compte.';
  } finally {
    isCreating.value = false;
  }
}

// ── Étape 2 : assignation de rôle ───────────────────────────────────────────────
const roleSearch = ref('');

const { data: rolesData, isPending: rolesLoading } = useQuery({
  queryKey: ['cu-roles'],
  queryFn: async () => {
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    params.append('sortBy', 'label');
    params.append('sortDir', 'ASC');
    const res = await axiosInstance.get(`roles/search?${params.toString()}`);
    return res.data;
  },
  enabled: computed(() => step.value === 2),
  staleTime: 5 * 60_000,
});
const allRoles = computed<any[]>(() => rolesData.value?.data ?? []);
const roles = computed<any[]>(() => {
  const q = roleSearch.value.trim().toLowerCase();
  if (!q) return allRoles.value;
  return allRoles.value.filter(
    (r) =>
      (r.label ?? '').toLowerCase().includes(q) ||
      (r.code ?? '').toLowerCase().includes(q),
  );
});

const selectedRoleIds = ref<Set<string>>(new Set());
const assignStoreId = ref('');
const assignOrgId = ref('');
const isAssigning = ref(false);
const assignError = ref('');
const assignSuccess = ref(false);

// ── Dropdown rôle personnalisé (recherche + pagination) ────────────────────────
const roleDropdownOpen = ref(false);
const ROLE_PAGE_SIZES = [10, 20, 50] as const;
const rolePageSize = ref<(typeof ROLE_PAGE_SIZES)[number]>(10);
const rolePage = ref(1);
watch([roleSearch, rolePageSize], () => {
  rolePage.value = 1;
});
const roleTotalPages = computed(() =>
  Math.max(1, Math.ceil(roles.value.length / rolePageSize.value)),
);
const pagedRoles = computed(() => {
  const start = (rolePage.value - 1) * rolePageSize.value;
  return roles.value.slice(start, start + rolePageSize.value);
});
const selectedRolesLabel = computed(() => {
  const picked = allRoles.value.filter((r: any) =>
    selectedRoleIds.value.has(r.id),
  );
  if (!picked.length) return '';
  if (picked.length === 1) return `${picked[0].label} (${picked[0].code})`;
  return t(
    'userManagement.create.rolesSelected',
    { n: picked.length },
    picked.length,
  );
});
function toggleRole(id: string) {
  const next = new Set(selectedRoleIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedRoleIds.value = next;
}

// ── Dropdown commerce personnalisé (recherche + pagination, optionnel) ─────────
const storeDropdownOpen = ref(false);
const storeSearch = ref('');
const STORE_PAGE_SIZES = [10, 20, 50] as const;
const storePageSize = ref<(typeof STORE_PAGE_SIZES)[number]>(10);
const storePage = ref(1);
watch([storeSearch, storePageSize], () => {
  storePage.value = 1;
});
const filteredStores = computed(() => {
  const q = storeSearch.value.trim().toLowerCase();
  if (!q) return restaurants.value;
  return restaurants.value.filter((r: any) =>
    (r.designation ?? r.name ?? '').toLowerCase().includes(q),
  );
});
const storeTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredStores.value.length / storePageSize.value)),
);
const pagedStores = computed(() => {
  const start = (storePage.value - 1) * storePageSize.value;
  return filteredStores.value.slice(start, start + storePageSize.value);
});
const selectedStoreLabel = computed(() => {
  const r = restaurants.value.find((r: any) => r.id === assignStoreId.value);
  return r ? (r.designation ?? r.name) : '';
});
function selectStore(id: string) {
  assignStoreId.value = id;
  storeDropdownOpen.value = false;
}
function clearStore() {
  assignStoreId.value = '';
  storeDropdownOpen.value = false;
}

// ── Dropdown organisation personnalisé (recherche + pagination, optionnel) ────
const orgDropdownOpen = ref(false);
const orgSearch = ref('');
const ORG_PAGE_SIZES = [10, 20, 50] as const;
const orgPageSize = ref<(typeof ORG_PAGE_SIZES)[number]>(10);
const orgPage = ref(1);
watch([orgSearch, orgPageSize], () => {
  orgPage.value = 1;
});
const filteredOrgs = computed(() => {
  const q = orgSearch.value.trim().toLowerCase();
  if (!q) return organizations.value;
  return organizations.value.filter((o: any) =>
    (o.name ?? o.designation ?? '').toLowerCase().includes(q),
  );
});
const orgTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredOrgs.value.length / orgPageSize.value)),
);
const pagedOrgs = computed(() => {
  const start = (orgPage.value - 1) * orgPageSize.value;
  return filteredOrgs.value.slice(start, start + orgPageSize.value);
});
const selectedOrgLabel = computed(() => {
  const o = organizations.value.find((o: any) => o.id === assignOrgId.value);
  return o ? (o.name ?? o.designation) : '';
});
function selectOrg(id: string) {
  assignOrgId.value = id;
  orgDropdownOpen.value = false;
}
function clearOrg() {
  assignOrgId.value = '';
  orgDropdownOpen.value = false;
}

async function submitAssign() {
  if (!selectedRoleIds.value.size || !createdUserId.value) {
    assignError.value = 'Choisissez au moins un rôle dans la liste.';
    return;
  }
  isAssigning.value = true;
  assignError.value = '';
  try {
    await UserService.assignUserRolesById(
      createdUserId.value,
      [...selectedRoleIds.value].map((roleId) => ({
        roleId,
        restaurantId: assignStoreId.value || undefined,
        organizationId: assignOrgId.value || undefined,
      })),
    );
    assignSuccess.value = true;
    await queryClient.invalidateQueries({ queryKey: ['um-users'] });
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    assignError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || "Erreur lors de l'assignation du rôle.";
  } finally {
    isAssigning.value = false;
  }
}

function finishAndOpenDetail() {
  if (createdUserId.value) emit('created', createdUserId.value);
  emit('close');
}
function close() {
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition name="cu-backdrop">
      <div v-if="open" class="cu-backdrop" @click="close" />
    </Transition>
    <Transition name="cu-drawer">
      <aside v-if="open" class="cu-drawer" @click.stop>
        <div
          class="cu"
          @click="
            roleDropdownOpen = false;
            storeDropdownOpen = false;
            orgDropdownOpen = false;
          "
        >
          <div class="cu__header">
            <button class="cu__back" @click="close">
              <i class="pi pi-times" />
            </button>
            <div>
              <h1 class="cu__title">{{ t('userManagement.create.title') }}</h1>
              <p class="cu__sub">
                {{
                  step === 1
                    ? t('userManagement.create.step1')
                    : t('userManagement.create.step2')
                }}
              </p>
            </div>
          </div>

          <!-- Stepper -->
          <div class="cu__stepper">
            <div
              class="cu__step"
              :class="{
                'cu__step--active': step === 1,
                'cu__step--done': step === 2,
              }"
            >
              <span class="cu__step-dot">1</span>
              <span class="cu__step-label">{{
                t('userManagement.create.step1')
              }}</span>
            </div>
            <div class="cu__step-line" />
            <div class="cu__step" :class="{ 'cu__step--active': step === 2 }">
              <span class="cu__step-dot">2</span>
              <span class="cu__step-label">{{
                t('userManagement.create.step2')
              }}</span>
            </div>
          </div>

          <div class="cu__body">
            <!-- Étape 1 -->
            <template v-if="step === 1">
              <div class="cu__field">
                <label class="cu__label">{{
                  t('userManagement.create.email')
                }}</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="cu__input"
                  placeholder="jean.dupont@exemple.com"
                  autocomplete="email"
                />
              </div>
              <div class="cu__field">
                <label class="cu__label">{{
                  t('userManagement.create.password')
                }}</label>
                <div class="cu__pwd-wrap">
                  <input
                    v-model="form.password"
                    :type="showPwd ? 'text' : 'password'"
                    class="cu__input"
                    placeholder="Min. 6 caractères"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="cu__pwd-eye"
                    @click="showPwd = !showPwd"
                  >
                    <i :class="showPwd ? 'pi pi-eye-slash' : 'pi pi-eye'" />
                  </button>
                </div>
                <button
                  type="button"
                  class="cu__gen-btn"
                  @click="generatePassword"
                >
                  <i class="pi pi-refresh" />
                  {{ t('userManagement.create.generate') }}
                </button>
              </div>
              <p v-if="createError" class="cu__error">
                <i class="pi pi-exclamation-circle" /> {{ createError }}
              </p>

              <div class="cu__actions">
                <button class="cu__btn cu__btn--ghost" @click="close">
                  {{ t('userManagement.create.cancel') }}
                </button>
                <button
                  class="cu__btn cu__btn--primary"
                  :disabled="isCreating"
                  @click="submitAccount"
                >
                  <span v-if="isCreating" class="cu__spinner" />
                  {{
                    isCreating
                      ? t('userManagement.create.creating')
                      : t('userManagement.create.next')
                  }}
                </button>
              </div>
            </template>

            <!-- Étape 2 -->
            <template v-else>
              <div v-if="assignSuccess" class="cu__success">
                <i class="pi pi-check-circle" />
                <p>{{ t('userManagement.create.success') }}</p>
                <div class="cu__actions cu__actions--center">
                  <button class="cu__btn cu__btn--ghost" @click="close">
                    {{ t('userManagement.title') }}
                  </button>
                  <button
                    class="cu__btn cu__btn--primary"
                    @click="finishAndOpenDetail"
                  >
                    {{ t('userManagement.create.done') }}
                  </button>
                </div>
              </div>
              <template v-else>
                <div class="cu__field" @click.stop>
                  <label class="cu__label">{{
                    t('userManagement.create.storeOptional')
                  }}</label>
                  <div class="cu__dropdown-wrap">
                    <button
                      class="cu__dropdown-btn"
                      :class="{ 'cu__dropdown-btn--active': assignStoreId }"
                      @click="storeDropdownOpen = !storeDropdownOpen"
                    >
                      <i class="pi pi-shop" />
                      <span>{{
                        selectedStoreLabel || t('userManagement.create.noStore')
                      }}</span>
                      <i
                        v-if="assignStoreId"
                        class="pi pi-times"
                        @click.stop="clearStore"
                      />
                      <i v-else class="pi pi-chevron-down" />
                    </button>
                    <Transition name="cu-drop">
                      <div v-if="storeDropdownOpen" class="cu__dropdown-panel">
                        <div class="cu__dropdown-search-wrap">
                          <i class="pi pi-search" />
                          <input
                            v-model="storeSearch"
                            class="cu__dropdown-search"
                            :placeholder="
                              t('userManagement.create.searchStore')
                            "
                          />
                        </div>
                        <div class="cu__dropdown-list">
                          <button
                            class="cu__dropdown-item"
                            :class="{
                              'cu__dropdown-item--active': !assignStoreId,
                            }"
                            @click="clearStore"
                          >
                            {{ t('userManagement.create.noStore') }}
                          </button>
                          <button
                            v-for="r in pagedStores"
                            :key="r.id"
                            class="cu__dropdown-item"
                            :class="{
                              'cu__dropdown-item--active':
                                assignStoreId === r.id,
                            }"
                            @click="selectStore(r.id)"
                          >
                            {{ r.designation ?? r.name }}
                          </button>
                          <p
                            v-if="!filteredStores.length"
                            class="cu__dropdown-empty"
                          >
                            {{ t('userManagement.create.noStoreFound') }}
                          </p>
                        </div>
                        <div class="cu__dropdown-footer">
                          <select
                            v-model.number="storePageSize"
                            class="cu__dropdown-pagesize"
                          >
                            <option
                              v-for="ps in STORE_PAGE_SIZES"
                              :key="ps"
                              :value="ps"
                            >
                              {{ ps }} / {{ t('userManagement.perPage') }}
                            </option>
                          </select>
                          <div
                            v-if="storeTotalPages > 1"
                            class="cu__dropdown-pager"
                          >
                            <button
                              class="cu__dropdown-pager-btn"
                              :disabled="storePage === 1"
                              @click="storePage--"
                            >
                              <i class="pi pi-chevron-left" />
                            </button>
                            <span>{{ storePage }} / {{ storeTotalPages }}</span>
                            <button
                              class="cu__dropdown-pager-btn"
                              :disabled="storePage === storeTotalPages"
                              @click="storePage++"
                            >
                              <i class="pi pi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Transition>
                  </div>
                </div>

                <div class="cu__field" @click.stop>
                  <label class="cu__label">{{
                    t('userManagement.create.orgOptional')
                  }}</label>
                  <div class="cu__dropdown-wrap">
                    <button
                      class="cu__dropdown-btn"
                      :class="{ 'cu__dropdown-btn--active': assignOrgId }"
                      @click="orgDropdownOpen = !orgDropdownOpen"
                    >
                      <i class="pi pi-building" />
                      <span>{{
                        selectedOrgLabel || t('userManagement.create.noOrg')
                      }}</span>
                      <i
                        v-if="assignOrgId"
                        class="pi pi-times"
                        @click.stop="clearOrg"
                      />
                      <i v-else class="pi pi-chevron-down" />
                    </button>
                    <Transition name="cu-drop">
                      <div v-if="orgDropdownOpen" class="cu__dropdown-panel">
                        <div class="cu__dropdown-search-wrap">
                          <i class="pi pi-search" />
                          <input
                            v-model="orgSearch"
                            class="cu__dropdown-search"
                            :placeholder="t('userManagement.create.searchOrg')"
                          />
                        </div>
                        <div class="cu__dropdown-list">
                          <button
                            class="cu__dropdown-item"
                            :class="{
                              'cu__dropdown-item--active': !assignOrgId,
                            }"
                            @click="clearOrg"
                          >
                            {{ t('userManagement.create.noOrg') }}
                          </button>
                          <button
                            v-for="o in pagedOrgs"
                            :key="o.id"
                            class="cu__dropdown-item"
                            :class="{
                              'cu__dropdown-item--active': assignOrgId === o.id,
                            }"
                            @click="selectOrg(o.id)"
                          >
                            {{ o.name ?? o.designation }}
                          </button>
                          <p
                            v-if="!filteredOrgs.length"
                            class="cu__dropdown-empty"
                          >
                            {{ t('userManagement.create.noOrgFound') }}
                          </p>
                        </div>
                        <div class="cu__dropdown-footer">
                          <select
                            v-model.number="orgPageSize"
                            class="cu__dropdown-pagesize"
                          >
                            <option
                              v-for="ps in ORG_PAGE_SIZES"
                              :key="ps"
                              :value="ps"
                            >
                              {{ ps }} / {{ t('userManagement.perPage') }}
                            </option>
                          </select>
                          <div
                            v-if="orgTotalPages > 1"
                            class="cu__dropdown-pager"
                          >
                            <button
                              class="cu__dropdown-pager-btn"
                              :disabled="orgPage === 1"
                              @click="orgPage--"
                            >
                              <i class="pi pi-chevron-left" />
                            </button>
                            <span>{{ orgPage }} / {{ orgTotalPages }}</span>
                            <button
                              class="cu__dropdown-pager-btn"
                              :disabled="orgPage === orgTotalPages"
                              @click="orgPage++"
                            >
                              <i class="pi pi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Transition>
                  </div>
                </div>

                <div class="cu__field" @click.stop>
                  <label class="cu__label"
                    >{{ t('userManagement.create.role') }}
                    <span class="cu__required">*</span></label
                  >
                  <div class="cu__dropdown-wrap">
                    <button
                      class="cu__dropdown-btn"
                      :class="{
                        'cu__dropdown-btn--active': selectedRoleIds.size,
                      }"
                      @click="roleDropdownOpen = !roleDropdownOpen"
                    >
                      <i class="pi pi-shield" />
                      <span>{{
                        selectedRolesLabel || t('userManagement.create.role')
                      }}</span>
                      <i class="pi pi-chevron-down" />
                    </button>
                    <Transition name="cu-drop">
                      <div v-if="roleDropdownOpen" class="cu__dropdown-panel">
                        <div class="cu__dropdown-search-wrap">
                          <i class="pi pi-search" />
                          <input
                            v-model="roleSearch"
                            class="cu__dropdown-search"
                            :placeholder="t('userManagement.create.searchRole')"
                          />
                        </div>
                        <div v-if="rolesLoading" class="cu__role-loading">
                          <i class="pi pi-spin pi-spinner" />
                        </div>
                        <div v-else class="cu__dropdown-list">
                          <button
                            v-for="r in pagedRoles"
                            :key="r.id"
                            class="cu__dropdown-item cu__dropdown-item--checkbox"
                            :class="{
                              'cu__dropdown-item--active': selectedRoleIds.has(
                                r.id,
                              ),
                            }"
                            @click="toggleRole(r.id)"
                          >
                            <i
                              class="pi"
                              :class="
                                selectedRoleIds.has(r.id)
                                  ? 'pi-check-square'
                                  : 'pi-stop'
                              "
                            />
                            {{ r.label }} ({{ r.code }})
                          </button>
                          <p v-if="!roles.length" class="cu__dropdown-empty">
                            {{ t('userManagement.create.noRoleFound') }}
                          </p>
                        </div>
                        <div v-if="roles.length" class="cu__dropdown-footer">
                          <select
                            v-model.number="rolePageSize"
                            class="cu__dropdown-pagesize"
                          >
                            <option
                              v-for="ps in ROLE_PAGE_SIZES"
                              :key="ps"
                              :value="ps"
                            >
                              {{ ps }} / {{ t('userManagement.perPage') }}
                            </option>
                          </select>
                          <div
                            v-if="roleTotalPages > 1"
                            class="cu__dropdown-pager"
                          >
                            <button
                              class="cu__dropdown-pager-btn"
                              :disabled="rolePage === 1"
                              @click="rolePage--"
                            >
                              <i class="pi pi-chevron-left" />
                            </button>
                            <span>{{ rolePage }} / {{ roleTotalPages }}</span>
                            <button
                              class="cu__dropdown-pager-btn"
                              :disabled="rolePage === roleTotalPages"
                              @click="rolePage++"
                            >
                              <i class="pi pi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Transition>
                  </div>
                </div>

                <p v-if="assignError" class="cu__error">
                  <i class="pi pi-exclamation-circle" /> {{ assignError }}
                </p>

                <div class="cu__actions">
                  <button
                    class="cu__btn cu__btn--ghost"
                    @click="finishAndOpenDetail"
                  >
                    {{ t('userManagement.create.skip') }}
                  </button>
                  <button
                    class="cu__btn cu__btn--primary"
                    :disabled="isAssigning || !selectedRoleIds.size"
                    @click="submitAssign"
                  >
                    <span v-if="isAssigning" class="cu__spinner" />
                    {{
                      isAssigning
                        ? t('userManagement.create.assigning')
                        : t('userManagement.create.assign')
                    }}
                  </button>
                </div>
              </template>
            </template>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.cu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(8, 12, 20, 0.45);
  backdrop-filter: blur(2px);
}
.cu-backdrop-enter-active,
.cu-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.cu-backdrop-enter-from,
.cu-backdrop-leave-to {
  opacity: 0;
}

.cu-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 901;
  width: min(480px, 100vw);
  background: #f4f6f9;
  box-shadow: -4px 0 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}
[data-theme='dark'] .cu-drawer {
  background: #0c1421;
}
.cu-drawer-enter-active,
.cu-drawer-leave-active {
  transition: transform 0.25s ease;
}
.cu-drawer-enter-from,
.cu-drawer-leave-to {
  transform: translateX(100%);
}

.cu {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.75rem;
  box-sizing: border-box;
  overflow-y: auto;
}

.cu__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.cu__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.cu__title {
  margin: 0 0 0.15rem;
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cu__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.cu__stepper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.5rem;
}
.cu__step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cu__step-dot {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
}
.cu__step--active .cu__step-dot {
  border-color: #ffd60a;
  color: #0b1320;
  background: rgba(255, 214, 10, 0.15);
}
.cu__step--done .cu__step-dot {
  border-color: #ffd60a;
  background: #ffd60a;
  color: #0b1320;
}
.cu__step-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
}
.cu__step--active .cu__step-label {
  color: var(--nxt-on-surface);
}
.cu__step-line {
  flex: 1;
  max-width: 3rem;
  height: 2px;
  background: var(--nxt-outline-variant);
}

.cu__body {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.cu__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cu__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.cu__required {
  color: var(--nxt-error);
}
.cu__input,
.cu__select {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}
.cu__input:focus,
.cu__select:focus {
  border-color: #ffd60a;
}

/* Dropdown personnalisé (recherche + pagination) — rôle / commerce */
.cu__dropdown-wrap {
  position: relative;
}
.cu__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-sizing: border-box;
}
.cu__dropdown-btn span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  white-space: nowrap;
}
.cu__dropdown-btn--active {
  border-color: #ffd60a;
  color: var(--nxt-on-surface);
}
.cu__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.cu__dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cu__dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.cu__dropdown-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.375rem;
}
.cu__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.cu__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.cu__dropdown-item--checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cu__dropdown-item--checkbox .pi {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cu__dropdown-item--checkbox.cu__dropdown-item--active .pi-check-square {
  color: #ffd60a;
}
.cu__dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}
.cu__dropdown-empty {
  margin: 0;
  padding: 0.75rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  text-align: center;
}
.cu__dropdown-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.cu__dropdown-pagesize {
  height: 1.875rem;
  padding: 0 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.75rem;
  font-family: inherit;
}
.cu__dropdown-pager {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.cu__dropdown-pager-btn {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.375rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.6875rem;
  cursor: pointer;
}
.cu__dropdown-pager-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.cu-drop-enter-active,
.cu-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.cu-drop-enter-from,
.cu-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.cu__pwd-wrap {
  position: relative;
}
.cu__pwd-eye {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  cursor: pointer;
  color: var(--nxt-on-surface-variant);
}
.cu__gen-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  align-self: flex-start;
  margin-top: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  border: 1px solid var(--nxt-outline-variant);
  background: none;
  font-size: 0.6875rem;
  font-weight: 700;
  font-family: inherit;
  color: #0b1320;
  cursor: pointer;
}

.cu__role-loading {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  color: var(--nxt-on-surface-muted);
}
.cu__role-empty {
  text-align: center;
  padding: 1rem;
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
}

.cu__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
}
.cu__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  text-align: center;
}
.cu__success i {
  font-size: 2.5rem;
  color: #10b510;
}

.cu__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.cu__actions--center {
  justify-content: center;
  border-top: none;
  padding-top: 0.5rem;
}
.cu__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.cu__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cu__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.cu__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.cu__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  animation: cu-spin 0.7s linear infinite;
}
@keyframes cu-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
[data-theme='dark'] .cu__header,
[data-theme='dark'] .cu__body {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cu__input,
[data-theme='dark'] .cu__select {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cu__input:focus,
[data-theme='dark'] .cu__select:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .cu__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cu__back {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
</style>
